(() => {
  if (window.SonicWarranty) return;
  let turnstileLoading;
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const hex = bytes => [...new Uint8Array(bytes)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  const tokens = new Map();
  const uploaded = new Map();
  async function request(endpoint, action, { token, body, query = {}, binary = false, method = 'POST', onBytes, signal } = {}) {
    const url = new URL(endpoint, location.origin);
    url.searchParams.set('action', action);
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
    const response = binary ? await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const abort = () => xhr.abort();
      xhr.open(method, url);
      xhr.timeout = 180000;
      xhr.setRequestHeader('X-Warranty-Token', token);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = event => onBytes?.(Math.min(body.size, event.loaded));
      xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: async () => JSON.parse(xhr.responseText || '{}') });
      xhr.onerror = xhr.ontimeout = () => reject(new TypeError('Upload connection interrupted'));
      xhr.onabort = () => reject(new DOMException('Upload interrupted', 'AbortError'));
      xhr.onloadend = () => signal?.removeEventListener('abort', abort);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) { reject(new DOMException('Upload interrupted', 'AbortError')); return; }
      xhr.send(body);
    }) : await fetch(url, { method, credentials: 'omit', headers: { ...(token ? { 'X-Warranty-Token': token } : {}), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) }, ...(body !== undefined ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(55000) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(result.error || (window.sonicStrings?.request_failed || "We couldn’t submit your request right now. Please try again."));
      error.customerMessage = window.sonicServerErrors?.[result.error] || error.message;
      error.status = response.status;
      error.warranty = true;
      throw error;
    }
    return result;
  }
  async function challenge(form, sitekey) {
    if (!sitekey) throw new Error((window.sonicStrings?.verification_unconfigured || "Verification is not configured."));
    if (!window.turnstile) {
      if (!turnstileLoading) {
        turnstileLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          const failed = () => { clearTimeout(timeout); turnstileLoading = null; script.remove(); reject(new Error((window.sonicStrings?.verification_load_failed || "Verification could not load."))); };
          const timeout = setTimeout(failed, 15000);
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
          script.onload = () => { clearTimeout(timeout); resolve(); };
          script.onerror = failed;
          document.head.append(script);
        });
      }
      await turnstileLoading;
    }
    let mount = form.querySelector('[data-warranty-verification]');
    if (!mount) { mount = document.createElement('div'); mount.dataset.warrantyVerification = ''; form.querySelector('[type="submit"]').parentElement.before(mount); }
    return new Promise((resolve, reject) => {
      let widget;
      const finish = (token, error) => { clearTimeout(timeout); if (widget !== undefined) window.turnstile.remove(widget); error ? reject(new Error((window.sonicStrings?.verification_required || "Please complete the verification and try again."))) : resolve(token); };
      const timeout = setTimeout(() => finish(null, true), 120000);
      widget = window.turnstile.render(mount, { sitekey, action: 'warranty', appearance: 'interaction-only', callback: token => finish(token), 'error-callback': () => finish(null, true), 'expired-callback': () => finish(null, true) });
    });
  }
  async function submit({ form, endpoint, sitekey, fields, files = [], onProgress = () => {}, renewed = false }) {
    onProgress({ stage: 'preparing' });
    const types = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', avi: 'video/x-msvideo' };
    const payload = { ...fields, files: files.map(file => ({ name: file.name, type: file.type || types[file.name.split('.').pop().toLowerCase()] || '', size: file.size })) };
    const fingerprint = hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify({ endpoint, payload, modified: files.map(file => file.lastModified) }))));
    const key = `sonic-warranty:${fingerprint}`;
    let token = tokens.get(key);
    try { token ||= sessionStorage.getItem(key); } catch (_) { /* Memory fallback if storage is blocked. */ }
    token ||= hex(crypto.getRandomValues(new Uint8Array(32)));
    tokens.set(key, token);
    try { sessionStorage.setItem(key, token); } catch (_) { /* Memory fallback. */ }
    let result;
    try { result = await request(endpoint, 'start', { body: { token, form: payload } }); }
    catch (error) {
      if (error.status === 410 && !renewed) {
        tokens.delete(key);
        try { sessionStorage.removeItem(key); } catch (_) { /* Memory fallback. */ }
        return submit({ form, endpoint, sitekey, fields, files, onProgress, renewed: true });
      }
      if (error.status !== 400 || !error.message.includes('verification')) throw error;
      onProgress({ stage: 'verification' });
      result = await request(endpoint, 'start', { body: { token, form: payload, challenge: await challenge(form, sitekey) } });
    }
    if (result.state === 'uploading') {
      if (!Number.isSafeInteger(result.chunkBytes) || result.chunkBytes < 1) throw new Error('Upload could not start');
      const previous = uploaded.get(token);
      const completed = previous?.files.length === files.length && previous.files.every((file, index) => file === files[index]) ? previous.parts : new Map();
      uploaded.set(token, { files, parts: completed });
      const loaded = new Map(completed), parts = [];
      const total = files.reduce((sum, file) => sum + file.size, 0);
      const report = () => {
        const fileBytes = files.map(() => 0);
        for (const [part, bytes] of loaded) fileBytes[Number(part.split(':')[0])] += bytes;
        onProgress({ stage: 'uploading', total, loaded: fileBytes.reduce((sum, bytes) => sum + bytes, 0), fileBytes, fileCount: files.length });
      };
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        for (let start = 0, chunk = 0; start < file.size; start += result.chunkBytes, chunk++) {
          const key = `${fileIndex}:${chunk}`;
          if (!completed.has(key)) parts.push({ key, body: file.slice(start, start + result.chunkBytes), file: fileIndex, chunk });
        }
      }
      report();
      const controller = new AbortController();
      let failed;
      const upload = async () => {
        while (parts.length && !failed) {
          const part = parts.shift();
          try {
            for (let attempt = 0; ; attempt++) {
              try {
                await request(endpoint, 'chunk', { token, binary: true, body: part.body, query: { file: part.file, chunk: part.chunk }, signal: controller.signal, onBytes: bytes => { loaded.set(part.key, bytes); report(); } });
                completed.set(part.key, part.body.size);
                loaded.set(part.key, part.body.size); report();
                break;
              } catch (error) {
                loaded.delete(part.key); report();
                if (controller.signal.aborted || attempt >= 2 || (error.status && error.status < 500 && error.status !== 408 && error.status !== 429)) throw error;
                onProgress({ stage: 'retrying' });
                await wait(1000 * (attempt + 1));
              }
            }
          } catch (error) { if (!failed) { failed = error; controller.abort(); } }
        }
      };
      // Two in-flight parts hide request/storage latency without flooding slow connections.
      await Promise.all([upload(), upload()]);
      if (failed) throw failed;
    }
    if (!['delivered', 'review', 'sending'].includes(result.state)) {
      onProgress({ stage: 'saving' });
      result = await request(endpoint, 'submit', { token, body: {} });
    }
    if (!['delivered', 'sending', 'review'].includes(result.state) || !result.reference) {
      throw new Error('Receipt could not be confirmed');
    }
    uploaded.delete(token);
    // The queue owns delivery after durable receipt; customers need not wait for Gorgias.
    return result;
  }
  window.SonicWarranty = { submit };
})();
